import { Role } from "../../../models";
import { OutsideEnrollmentAreaStatus, Request } from "../../../models/Request";

const institutionRoles = [
  Role.CAS,
  Role.Helpdesk,
  Role.InstitutionAdministrator,
  Role.InstitutionPrincipal,
];

const countyAndRegionalRoles = [
  Role.CAS,
  Role.Helpdesk,
  Role.CountyLeader,
  Role.CountyAdministrator,
  Role.CountyCommittee,
  Role.RegionalCommittee,
];

type StatusHandlerResult = {
  nextStatus: OutsideEnrollmentAreaStatus; // For determining what status is sent when clicked on the save button
  isApproved: boolean; // For determining if the request is approved through item.suggestedEnrollmentOutsideEnrollmentArea
  shouldEdit: boolean; // For determining if the request should be instanstlly put into edit mode
  shouldSetEdited: boolean; // For determining if the request should be set as edited
  hasFinalDecision: boolean; // For determining if the request has a final decision
  canEdit: boolean; // For determining if the role is allow to edit
};

const defaultResult: StatusHandlerResult = {
  nextStatus: OutsideEnrollmentAreaStatus.NotProcessed,
  isApproved: false,
  shouldEdit: false,
  shouldSetEdited: false,
  hasFinalDecision: false,
  canEdit: false,
};

/**
 * Handles NotProcessed status transitions
 */
export const handleNotProcessedStatus = (
  request: Request,
  currentRole?: Role
): StatusHandlerResult => {
  const result = { ...defaultResult };

  // Institution roles can suggest enrollment from NotProcessed state
  if (currentRole && institutionRoles.includes(currentRole)) {
    result.isApproved =
      request.suggestedEnrollmentOutsideEnrollmentArea ?? false;

    result.nextStatus = result.isApproved
      ? OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted
      : OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected;
    result.shouldEdit = true;
    result.shouldSetEdited = true;
    result.hasFinalDecision = false;
    result.canEdit = true; // Institution roles can edit in NotProcessed state
  }

  return result;
};

/**
 * Handles SuggestedEnrollment status transitions
 */
export const handleSuggestedEnrollmentStatus = (
  request: Request,
  currentRole: Role | undefined
): StatusHandlerResult => {
  const result: StatusHandlerResult = { ...defaultResult };

  // Institution roles can change their suggestion
  if (
    currentRole &&
    [Role.InstitutionAdministrator, Role.InstitutionPrincipal].includes(
      currentRole
    )
  ) {
    result.nextStatus =
      request.outsideEnrollmentAreaStatus ??
      OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted;
    result.isApproved =
      request.suggestedEnrollmentOutsideEnrollmentArea ?? false;
    result.canEdit = true; // Institution roles can edit in SuggestedEnrollment state
  }
  // County and regional roles can make the final decision
  else if (currentRole && countyAndRegionalRoles.includes(currentRole)) {
    result.isApproved =
      request.approvedEnrollmentOutsideEnrollmentArea ?? false;
    result.nextStatus = result.isApproved
      ? OutsideEnrollmentAreaStatus.DecisionAccepted
      : OutsideEnrollmentAreaStatus.DecisionDeclined;
    result.shouldSetEdited = true;
    result.shouldEdit = true;
    result.hasFinalDecision = true;
    result.canEdit = true; // County and regional roles can edit in SuggestedEnrollment state
  }

  return result;
};

/**
 * Handles Decision status transitions
 */
export const handleDecisionStatus = (
  request: Request,
  currentRole?: Role
): StatusHandlerResult => {
  const result = { ...defaultResult };

  // County and regional roles can change their decision
  if (currentRole && countyAndRegionalRoles.includes(currentRole)) {
    result.nextStatus =
      request.outsideEnrollmentAreaStatus ??
      OutsideEnrollmentAreaStatus.DecisionDeclined;
    result.isApproved =
      request.approvedEnrollmentOutsideEnrollmentArea ?? false;
    result.hasFinalDecision = true;
    result.canEdit = true; // County and regional roles can edit in Decision state
  }

  return result;
};

/**
 * Handles status transitions based on the current status and role
 */
export const handleStatusTransition = (
  request: Request,
  currentRole?: Role
): StatusHandlerResult => {
  const status =
    request.outsideEnrollmentAreaStatus ??
    OutsideEnrollmentAreaStatus.NotProcessed;

  switch (status) {
    case OutsideEnrollmentAreaStatus.NotProcessed:
      return handleNotProcessedStatus(request, currentRole);
    case OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected:
    case OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted:
      return handleSuggestedEnrollmentStatus(request, currentRole);
    case OutsideEnrollmentAreaStatus.DecisionAccepted:
    case OutsideEnrollmentAreaStatus.DecisionDeclined:
      return handleDecisionStatus(request, currentRole);
    default:
      return defaultResult;
  }
};
